import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

import Typography from '@mui/material/Typography';
import useStyles from '../styles';
import SubSensors from '../Widgets/SensorConfig/SubSensors';
import AccessPoints from '../Widgets/SensorConfig/AccessPoints';
import {
  SensorLatest,
  VisibleSubSensor,
  BaseAccessPoint,
  WiFiConfig,
  GatewaySensorType,
  BacnetSettings,
} from '../services/api/api';
import {
  deleteSensor,
  fetchSensorLatest,
  fetchVisibleItems,
  getSensorBacnetConfig,
} from '../services/apiService';
import Loading from '../components/Loading';

import RemoveDialog from '../Widgets/Dialog/RemoveDialog';
import Header from '../components/Header';
import SensorIcon from '../styles/icons/SensorIcon';
import { sensorTypeDetails } from '../utils/sensorProperties';
import WifiIcon from '../styles/icons/WifiIcon';
import { setSensorsById, removeSensorData } from '../state/actions';
import { useAppDispatch } from '../state/store';
import SensorDetailsContainer from '../Widgets/SensorConfig/SensorDetails/SensorDetailsContainer';

export interface BacnetState {
  config: BacnetSettings | undefined;
  isEditable: boolean;
  alertMsg: string;
}

function SensorConfig(): JSX.Element {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sensorId } = useParams() as { sensorId: string };

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [fetchingVisibleItems, setFetchingVisibleItems] = useState<boolean>(false);
  const [sensorDetails, setSensorDetails] = useState<SensorLatest>();
  const [subSensors, setSubSensors] = useState<VisibleSubSensor[]>();
  const [accessPoints, setAccessPoints] = useState<BaseAccessPoint[]>();
  const [wifiConfig, setWifiConfig] = useState<WiFiConfig>();
  const [sensorExists, setSensorExists] = useState<boolean>(true);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [bacnetState, setBacnetState] = useState<BacnetState>();
  const [sensorIsGateway, setSensorIsGateway] = useState<boolean>();

  const locationId = sensorDetails?.location;

  const showHeaderTitle = () => {
    const Icon = sensorIsGateway
      ? WifiIcon
      : (sensorDetails?.type && sensorTypeDetails[sensorDetails.type]?.icon) ?? SensorIcon;

    const sensorType = sensorIsGateway
      ? 'Gateway Sensor'
      : sensorDetails?.type && sensorTypeDetails[sensorDetails.type]?.label;
    const sensorTitle = sensorDetails ? `${sensorDetails.name} - ${sensorType}` : sensorId;

    return (
      <Box sx={{ display: 'flex', padding: '8px' }}>
        <Icon />
        <Typography variant="h6" sx={{ marginLeft: '10px' }}>
          {sensorTitle}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    getSensorBacnetConfig(sensorId)
      .then((config) => {
        setBacnetState({
          config,
          isEditable: true,
          alertMsg: '',
        });
      })
      .catch((err) => {
        const status = err?.cause?.status ?? undefined;
        setBacnetState({
          config: undefined,
          isEditable: status === 404,
          alertMsg: err?.cause?.msg ?? '',
        });
      });
  }, [sensorId]);

  const getVisibleItems = useCallback(() => {
    setFetchingVisibleItems(true);
    fetchVisibleItems(sensorId)
      .then((response) => {
        setFetchingVisibleItems(false);
        setSubSensors(response.subsensors);
        setAccessPoints(response.wifi_aps);
        setWifiConfig(response.wifi_config);
      })
      .catch((err) => {
        enqueueSnackbar(err.cause, { variant: 'error', persist: true });
        setFetchingVisibleItems(false);
      });
  }, [sensorId]);

  const getSensorLatest = useCallback(() => {
    fetchSensorLatest(sensorId)
      .then((details) => {
        dispatch(setSensorsById([details]));
        const isGateway = Object.values(GatewaySensorType).includes(
          details?.type as GatewaySensorType
        );
        setSensorIsGateway(isGateway);
        setSensorDetails(details);
        setPageLoading(false);
        if (isGateway) getVisibleItems();
        setSensorExists(true);
      })
      .catch(() => {
        setPageLoading(false);
        setSensorExists(false);
      });
  }, [dispatch, getVisibleItems, sensorId]);

  useEffect(() => {
    setSensorExists(false);
    setPageLoading(true);
    getSensorLatest();
  }, [getSensorLatest, sensorId]);

  const removeSensor = () => {
    closeSnackbar();
    deleteSensor(sensorId, true)
      .then(() => {
        dispatch(removeSensorData(sensorId));
        navigate(`/location/${encodeURIComponent(locationId ?? '#')}`);
        setShowDialog(false);
        enqueueSnackbar('Sensor deleted successfully', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar(err.cause, { variant: 'error', persist: true });
        setShowDialog(false);
      });
  };

  return (
    <div className={`${classes.bodyContent} ${classes.sensorGrid}`} style={{ height: 'auto' }}>
      <Helmet>
        <title>{`${sensorDetails?.name ?? 'Sensor Config'} | LightFi `}</title>
      </Helmet>
      <RemoveDialog
        showDialog={showDialog}
        title={sensorId}
        removeHandler={removeSensor}
        cancelHandler={() => setShowDialog(false)}
        helperText="This will permanently delete all sensor data. Note: This operation may take some time to complete, you will not be able to create this sensor again during this time."
      />
      <Header>{showHeaderTitle()}</Header>
      <Divider style={{ margin: '5px 0' }} />
      {sensorExists && sensorDetails ? (
        <>
          <SensorDetailsContainer
            sensorDetails={sensorDetails}
            refreshDetails={() => getSensorLatest()}
            subSensors={subSensors}
            showDialog={() => setShowDialog(true)}
            bacnetState={bacnetState}
          />
          {sensorIsGateway && subSensors && subSensors?.length > 0 && (
            <Box sx={{ marginTop: '5px' }}>
              <SubSensors
                sensorGatewayId={sensorId}
                sensorGatewayDetails={sensorDetails}
                subSensors={subSensors}
                refreshVisibleItems={getVisibleItems}
                bacnetConfig={bacnetState?.config}
              />
            </Box>
          )}
          {sensorIsGateway && accessPoints && accessPoints.length > 0 && wifiConfig && (
            <AccessPoints
              accessPoints={accessPoints}
              wifiConfig={wifiConfig}
              refreshVisibleItems={getVisibleItems}
              sensorGatewayId={sensorId}
            />
          )}
          {fetchingVisibleItems && <Loading />}
        </>
      ) : (
        <>
          {pageLoading && <Loading />}
          {!pageLoading && (
            <Alert severity="error" className={classes.alertMsg}>
              Sensor not found
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

export default SensorConfig;
